import React, { useState, useEffect } from 'react';
import { X, Save, Copy, Calendar } from 'lucide-react';
import moment from 'moment';
import adminCalendarService from '../../services/adminCalendarService';

const CreateAgencyEventModal = ({ onClose, onEventCreated, editEvent = null }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'agency_event',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: '',
    start_time: '09:00',
    end_time: '10:00',
    all_day: false,
    location: '',
    reminder_minutes: 60,
    color: '#dc2626',
    target_type: 'all_agents',
    target_agents: [],
    is_mandatory: false,
    requires_response: true,
    send_notification: true,
    // Recurring event fields
    is_recurring: false,
    recurrence_pattern: 'weekly', // daily, weekly, monthly
    recurrence_end_date: '',
    recurrence_days: [], // For weekly: [0,1,2,3,4,5,6]
    // Template fields
    save_as_template: false,
    template_name: ''
  });

  useEffect(() => {
    loadAgents();
    loadTemplates();
    
    if (editEvent) {
      populateEditForm(editEvent);
    }
  }, [editEvent]);

  const loadAgents = async () => {
    try {
      const data = await adminCalendarService.getAgentsList();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadTemplates = () => {
    // Load saved templates from localStorage
    const saved = localStorage.getItem('eventTemplates');
    if (saved) {
      setTemplates(JSON.parse(saved));
    }
  };

  const populateEditForm = (event) => {
    const eventData = event.resource || event;
    
    // Safely parse target_agents - it might already be an array or a JSON string
    let targetAgents = [];
    if (eventData.target_agents) {
      if (Array.isArray(eventData.target_agents)) {
        targetAgents = eventData.target_agents;
      } else if (typeof eventData.target_agents === 'string') {
        try {
          targetAgents = JSON.parse(eventData.target_agents);
        } catch (e) {
          console.error('Error parsing target_agents:', e);
          targetAgents = [];
        }
      }
    }
    
    setFormData({
      ...formData,
      title: eventData.title || '',
      description: eventData.description || '',
      event_type: eventData.event_type || 'agency_event',
      start_date: moment(event.start).format('YYYY-MM-DD'),
      end_date: eventData.end_date ? moment(eventData.end_date).format('YYYY-MM-DD') : '',
      start_time: eventData.start_time || '09:00',
      end_time: eventData.end_time || '10:00',
      all_day: eventData.all_day || false,
      location: eventData.location || '',
      reminder_minutes: eventData.reminder_minutes || 60,
      color: eventData.color || '#dc2626',
      target_type: targetAgents.length > 0 ? 'specific_agents' : 'all_agents',
      target_agents: targetAgents,
      is_mandatory: eventData.is_mandatory || false,
      requires_response: eventData.agent_response === 'pending'
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-set color based on event type
    if (name === 'event_type') {
      const colors = {
        'agency_event': '#dc2626',
        'training': '#f97316',
        'meeting': '#6b7280',
        'deadline': '#eab308'
      };
      setFormData(prev => ({ ...prev, color: colors[value] || '#dc2626' }));
    }
  };

  const handleAgentSelection = (agentId) => {
    setFormData(prev => ({
      ...prev,
      target_agents: prev.target_agents.includes(agentId)
        ? prev.target_agents.filter(id => id !== agentId)
        : [...prev.target_agents, agentId]
    }));
  };

  const handleSelectAllAgents = () => {
    setFormData(prev => ({
      ...prev,
      target_agents: prev.target_agents.length === agents.length 
        ? [] 
        : agents.map(a => a.id)
    }));
  };

  const handleRecurrenceDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(day)
        ? prev.recurrence_days.filter(d => d !== day)
        : [...prev.recurrence_days, day]
    }));
  };

  const loadTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      ...template,
      start_date: moment().format('YYYY-MM-DD'),
      end_date: '',
      save_as_template: false,
      template_name: ''
    }));
  };

  const saveTemplate = () => {
    if (!formData.template_name) {
      alert('Please enter a template name');
      return;
    }

    const template = {
      name: formData.template_name,
      title: formData.title,
      description: formData.description,
      event_type: formData.event_type,
      start_time: formData.start_time,
      end_time: formData.end_time,
      all_day: formData.all_day,
      location: formData.location,
      reminder_minutes: formData.reminder_minutes,
      color: formData.color,
      target_type: formData.target_type,
      is_mandatory: formData.is_mandatory,
      requires_response: formData.requires_response,
      is_recurring: formData.is_recurring,
      recurrence_pattern: formData.recurrence_pattern,
      recurrence_days: formData.recurrence_days
    };

    const updatedTemplates = [...templates, template];
    localStorage.setItem('eventTemplates', JSON.stringify(updatedTemplates));
    setTemplates(updatedTemplates);
    alert('Template saved successfully!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title) {
      alert('Please enter an event title');
      return;
    }

    if (formData.target_type === 'specific_agents' && formData.target_agents.length === 0) {
      alert('Please select at least one agent');
      return;
    }

    if (formData.is_recurring && !formData.recurrence_end_date) {
      alert('Please specify when the recurring event should end');
      return;
    }

    if (formData.is_recurring && formData.recurrence_pattern === 'weekly' && formData.recurrence_days.length === 0) {
      alert('Please select at least one day for weekly recurrence');
      return;
    }

    setLoading(true);

    try {
      // If editing an existing event, update it instead of creating new
      if (editEvent) {
        await updateExistingEvent();
        onEventCreated();
        return;
      }

      // Save as template if requested
      if (formData.save_as_template) {
        saveTemplate();
      }

      // Handle recurring events
      if (formData.is_recurring) {
        await createRecurringEvents();
      } else {
        // Single event
        await createSingleEvent();
      }

      onEventCreated();
    } catch (error) {
      console.error(editEvent ? 'Error updating event:' : 'Error creating event:', error);
      alert(editEvent ? 'Failed to update event. Please try again.' : 'Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateExistingEvent = async () => {
    const eventId = editEvent.resource?.id || editEvent.id;
    const updateData = {
      title: formData.title,
      description: formData.description,
      event_type: formData.event_type,
      start_date: formData.start_date,
      end_date: formData.end_date || formData.start_date,
      start_time: formData.all_day ? null : formData.start_time,
      end_time: formData.all_day ? null : formData.end_time,
      all_day: formData.all_day,
      location: formData.location,
      reminder_minutes: formData.reminder_minutes,
      color: formData.color,
      is_mandatory: formData.is_mandatory
    };

    await adminCalendarService.updateEvent(eventId, updateData);
  };

  const createSingleEvent = async () => {
    const eventData = {
      title: formData.title,
      description: formData.description,
      event_type: formData.event_type,
      start_date: formData.start_date,
      end_date: formData.end_date || formData.start_date,
      start_time: formData.all_day ? null : formData.start_time,
      end_time: formData.all_day ? null : formData.end_time,
      all_day: formData.all_day,
      location: formData.location,
      reminder_minutes: formData.reminder_minutes,
      color: formData.color,
      target_type: formData.target_type,
      target_agents: formData.target_type === 'specific_agents' ? formData.target_agents : undefined,
      is_mandatory: formData.is_mandatory,
      requires_response: formData.requires_response
    };

    await adminCalendarService.createEvent(eventData);
  };

  const createRecurringEvents = async () => {
    const startDate = moment(formData.start_date);
    const endDate = moment(formData.recurrence_end_date);
    const events = [];

    let currentDate = startDate.clone();

    while (currentDate.isSameOrBefore(endDate)) {
      // For weekly recurrence, check if current day is selected
      if (formData.recurrence_pattern === 'weekly') {
        if (formData.recurrence_days.includes(currentDate.day())) {
          events.push(createEventData(currentDate));
        }
        currentDate.add(1, 'day');
      } else if (formData.recurrence_pattern === 'daily') {
        events.push(createEventData(currentDate));
        currentDate.add(1, 'day');
      } else if (formData.recurrence_pattern === 'monthly') {
        events.push(createEventData(currentDate));
        currentDate.add(1, 'month');
      }
    }

    // Create all recurring events
    for (const eventData of events) {
      await adminCalendarService.createEvent(eventData);
    }
  };

  const createEventData = (date) => ({
    title: formData.title,
    description: formData.description,
    event_type: formData.event_type,
    start_date: date.format('YYYY-MM-DD'),
    end_date: date.format('YYYY-MM-DD'),
    start_time: formData.all_day ? null : formData.start_time,
    end_time: formData.all_day ? null : formData.end_time,
    all_day: formData.all_day,
    location: formData.location,
    reminder_minutes: formData.reminder_minutes,
    color: formData.color,
    target_type: formData.target_type,
    target_agents: formData.target_type === 'specific_agents' ? formData.target_agents : undefined,
    is_mandatory: formData.is_mandatory,
    requires_response: formData.requires_response
  });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold">
            {editEvent ? 'Edit Event' : 'Create Agency Event'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Templates Section */}
          {templates.length > 0 && !editEvent && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Load from Template
              </label>
              <div className="flex flex-wrap gap-2">
                {templates.map((template, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => loadTemplate(template)}
                    className="px-3 py-1 bg-white border border-blue-300 rounded-md text-sm hover:bg-blue-100"
                  >
                    <Copy className="w-3 h-3 inline mr-1" />
                    {template.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Monthly Team Meeting"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type *
              </label>
              <select
                name="event_type"
                value={formData.event_type}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="agency_event">Agency Event</option>
                <option value="training">Training</option>
                <option value="meeting">Meeting</option>
                <option value="deadline">Deadline</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-full h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Event details..."
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Date & Time</h4>
            
            <div className="mb-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="all_day"
                  checked={formData.all_day}
                  onChange={handleChange}
                  className="mr-2"
                />
                All Day Event
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  min={formData.start_date}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {!formData.all_day && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Recurring Events */}
          {!editEvent && (
            <div className="border-t pt-4">
              <div className="mb-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_recurring"
                    checked={formData.is_recurring}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="font-medium text-gray-900">Recurring Event</span>
                </label>
              </div>

              {formData.is_recurring && (
                <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Repeat Pattern
                      </label>
                      <select
                        name="recurrence_pattern"
                        value={formData.recurrence_pattern}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date *
                      </label>
                      <input
                        type="date"
                        name="recurrence_end_date"
                        value={formData.recurrence_end_date}
                        onChange={handleChange}
                        min={formData.start_date}
                        required={formData.is_recurring}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {formData.recurrence_pattern === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Repeat On
                      </label>
                      <div className="flex gap-2">
                        {weekDays.map((day, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleRecurrenceDayToggle(index)}
                            className={`px-3 py-2 rounded-md text-sm font-medium ${
                              formData.recurrence_days.includes(index)
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Location & Reminder */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Conference Room A"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reminder
              </label>
              <select
                name="reminder_minutes"
                value={formData.reminder_minutes}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={15}>15 minutes before</option>
                <option value={30}>30 minutes before</option>
                <option value={60}>1 hour before</option>
                <option value={120}>2 hours before</option>
                <option value={1440}>1 day before</option>
              </select>
            </div>
          </div>

          {/* Target Agents */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Target Agents *</h4>
            
            <div className="space-y-3">
              <div>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="target_type"
                    value="all_agents"
                    checked={formData.target_type === 'all_agents'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  All Agents ({agents.length})
                </label>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="target_type"
                    value="specific_agents"
                    checked={formData.target_type === 'specific_agents'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Specific Agents
                </label>
              </div>

              {formData.target_type === 'specific_agents' && (
                <div className="pl-6 border-l-2 border-blue-200">
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={handleSelectAllAgents}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {formData.target_agents.length === agents.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                    {agents.map((agent) => (
                      <label key={agent.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_agents.includes(agent.id)}
                          onChange={() => handleAgentSelection(agent.id)}
                          className="mr-2"
                        />
                        {agent.name} ({agent.email})
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Event Options */}
          <div className="border-t pt-4 space-y-3">
            <h4 className="font-medium text-gray-900 mb-3">Event Options</h4>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_mandatory"
                checked={formData.is_mandatory}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm">
                <strong>Mandatory Event</strong> - Agents must acknowledge this event
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="requires_response"
                checked={formData.requires_response}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm">
                <strong>Requires Response</strong> - Agents must Accept/Decline/Tentative
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="send_notification"
                checked={formData.send_notification}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm">
                <strong>Send Email Notification</strong> - Notify agents via email
              </span>
            </label>

            {!editEvent && (
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="save_as_template"
                  checked={formData.save_as_template}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm">
                  <strong>Save as Template</strong> - Reuse this event configuration
                </span>
              </label>
            )}

            {formData.save_as_template && (
              <div className="pl-6">
                <input
                  type="text"
                  name="template_name"
                  value={formData.template_name}
                  onChange={handleChange}
                  placeholder="Template name (e.g., Weekly Team Meeting)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {editEvent ? 'Update Event' : 'Create Event'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAgencyEventModal;
